import { Alert, AlertDescription, AlertTitle } from './ui/alert';
import { Badge } from './ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './ui/accordion';
import { CheckCircle2, AlertTriangle, HelpCircle, Server, Database, Shield } from 'lucide-react';
import type { AuthorityMappingArtifact } from '../types';

interface AuthorityMappingSummaryProps {
  artifact: AuthorityMappingArtifact;
}

export function AuthorityMappingSummary({ artifact }: AuthorityMappingSummaryProps) {
  if (!artifact || !artifact.entries || !artifact.entries.length) return null;

  const getConfidenceBadge = (level: 'high' | 'medium' | 'low') => {
    switch (level) {
      case 'high':
        return <Badge className="bg-green-600">High Confidence</Badge>;
      case 'medium':
        return <Badge className="bg-yellow-600">Medium Confidence</Badge>;
      case 'low':
        return <Badge variant="destructive">Low Confidence</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      {/* Summary Notes */}
      {artifact.summaryNotes && artifact.summaryNotes.length > 0 && (
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Authority Status</AlertTitle>
          <AlertDescription>
            <ul className="list-disc list-inside space-y-1 mt-1">
              {artifact.summaryNotes.map((note, idx) => (
                <li key={idx}>{note}</li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      {/* Entries */}
      <div className="grid gap-4">
        {artifact.entries.map((entry) => (
          <Card key={entry.artifactId} className="overflow-hidden">
            <CardHeader className="bg-muted/30 pb-3">
              <div className="flex items-start justify-between">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2">
                    {entry.assetTypeKey === 'people' ? <Shield className="h-4 w-4" /> : 
                     entry.assetTypeKey === 'windows' ? <Server className="h-4 w-4" /> : 
                     <Database className="h-4 w-4" />}
                    {entry.assetType}
                  </CardTitle>
                  <p className="text-sm text-muted-foreground mt-1">
                    Primary Authority: <span className="font-semibold text-foreground">{entry.primaryAuthority}</span>
                  </p>
                </div>
                {getConfidenceBadge(entry.confidenceLevel)}
              </div>
            </CardHeader>
            <CardContent className="pt-4">
              <div className="space-y-4">
                {/* Rationale */}
                <div className="text-sm bg-blue-50 text-blue-800 p-3 rounded-md border border-blue-100">
                  {entry.authorityRationale}
                </div>

                {/* Secondary Sources */}
                {entry.secondarySources.length > 0 && (
                  <div>
                    <h4 className="text-xs font-semibold uppercase text-muted-foreground mb-1">Secondary Sources</h4>
                    <div className="flex flex-wrap gap-1">
                      {entry.secondarySources.map((source, idx) => (
                        <Badge key={idx} variant="outline">{source}</Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Conflict Strategy */}
                <div>
                  <h4 className="text-xs font-semibold uppercase text-muted-foreground mb-1">Conflict Strategy</h4>
                  <p className="text-sm">{entry.conflictStrategy}</p>
                </div>

                {/* Attribute Level Mapping */}
                {entry.attributeAuthorities && entry.attributeAuthorities.length > 0 && (
                  <Accordion type="single" collapsible className="w-full border rounded-md">
                    <AccordionItem value="attributes" className="border-b-0">
                      <AccordionTrigger className="px-4 py-2 hover:no-underline hover:bg-muted/50">
                        <span className="text-sm font-medium">Attribute-Level Mapping ({entry.attributeAuthorities.length})</span>
                      </AccordionTrigger>
                      <AccordionContent>
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Attribute</TableHead>
                              <TableHead>Authority</TableHead>
                              <TableHead>Confidence</TableHead>
                              <TableHead>Notes</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {entry.attributeAuthorities.map((attr) => {
                                // Extract notes from derived answers. 
                                // The derivedFromAnswers object has keys like `dam_{os}_attr_{attr}_notes`
                                // We can find any key that ends with '_notes'
                                const notesKey = Object.keys(attr.derivedFromAnswers).find(k => k.endsWith('_notes'));
                                const notes = notesKey ? attr.derivedFromAnswers[notesKey] : '-';
                                
                                return (
                                  <TableRow key={attr.attributeKey}>
                                    <TableCell className="font-medium">{attr.attributeLabel}</TableCell>
                                    <TableCell>{attr.primaryAuthority}</TableCell>
                                    <TableCell>
                                      {attr.confidenceLevel === 'high' ? <div className="flex items-center gap-1 text-green-600"><CheckCircle2 className="h-4 w-4" /> High</div> :
                                      attr.confidenceLevel === 'medium' ? <div className="flex items-center gap-1 text-yellow-600"><HelpCircle className="h-4 w-4" /> Medium</div> :
                                      <div className="flex items-center gap-1 text-red-600"><AlertTriangle className="h-4 w-4" /> Low</div>}
                                    </TableCell>
                                    <TableCell className="text-muted-foreground italic max-w-[200px] truncate" title={notes === '-' ? '' : notes}>
                                      {notes}
                                    </TableCell>
                                  </TableRow>
                                );
                            })}
                          </TableBody>
                        </Table>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}